import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Nav, Tab } from "react-bootstrap";
import FetchingSpinner from "../../../common/FetchingSpinner";
import "../../company/companyOffices/officeDashboard/analytics.scss";
import "./dashboardStatistics.scss";
import DashboardPeriodPicker from "./DashboardPeriodPicker";
import AdminDashboardView from "./AdminDashboardView";
import ManagerDashboardView from "./ManagerDashboardView";
import UserDashboardView from "./UserDashboardView";
import TodayDashboardView from "./TodayDashboardView";
import useDashboardSummary, { getCurrentMonthPeriod } from "./useDashboardSummary";
import { validatePeriod } from "./dashboardStatisticsUtils";

const VIEW_BY_ROLE = {
  admin: AdminDashboardView,
  manager: ManagerDashboardView,
  user: UserDashboardView,
};

const RoleDashboardStats = () => {
  const { t } = useTranslation();
  const defaultPeriod = getCurrentMonthPeriod();
  const [activeTab, setActiveTab] = useState("today");
  const [activePresetId, setActivePresetId] = useState("thisMonth");
  const [draftFrom, setDraftFrom] = useState(defaultPeriod.periodFrom);
  const [draftTo, setDraftTo] = useState(defaultPeriod.periodTo);
  const [appliedFrom, setAppliedFrom] = useState(defaultPeriod.periodFrom);
  const [appliedTo, setAppliedTo] = useState(defaultPeriod.periodTo);

  const todayQuery = useDashboardSummary(null, null, { usePeriod: false, enabled: activeTab === "today" });
  const periodQuery = useDashboardSummary(appliedFrom, appliedTo, { usePeriod: true, enabled: activeTab === "period" });

  const activeQuery = activeTab === "today" ? todayQuery : periodQuery;
  const { data, loading, error, roleType } = activeQuery;
  const ViewComponent = VIEW_BY_ROLE[roleType] ?? UserDashboardView;

  const applyPeriod = (from, to, presetId) => {
    if (!validatePeriod(from, to).valid) return;
    setAppliedFrom(from);
    setAppliedTo(to);
    setActivePresetId(presetId ?? null);
  };

  const handleApplyPeriod = () => {
    if (validatePeriod(draftFrom, draftTo).valid) {
      applyPeriod(draftFrom, draftTo, activePresetId);
    }
  };

  const handlePresetSelect = (presetId, from, to) => {
    setDraftFrom(from);
    setDraftTo(to);
    applyPeriod(from, to, presetId);
  };

  if (activeQuery.loading && !data) {
    return (
      <div className="py-4">
        <FetchingSpinner text={t("app.analytics.loading")} />
      </div>
    );
  }

  return (
    <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || "today")}>
      <Nav variant="tabs" className="dashboard-stats-tabs mb-3 border-bottom">
        <Nav.Item>
          <Nav.Link eventKey="today">
            <i className="bi bi-calendar-day me-1" />
            {t("app.analytics.tabToday")}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="period">
            <i className="bi bi-calendar-range me-1" />
            {t("app.analytics.tabPeriod")}
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <Tab.Content className="dashboard-stats-tab-content">
        <Tab.Pane eventKey="today">
          {error && (
            <div className="alert alert-danger mb-4">
              <i className="bi bi-exclamation-triangle me-2" />
              {t("app.analytics.errorLabel")}: {error}
            </div>
          )}
          {loading && data && (
            <div className="text-muted small mb-2">
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              {t("app.analytics.loading")}
            </div>
          )}
          {!error && data && <TodayDashboardView data={data} roleType={roleType} />}
        </Tab.Pane>

        <Tab.Pane eventKey="period">
          <DashboardPeriodPicker
            periodFrom={draftFrom}
            periodTo={draftTo}
            onPeriodFromChange={(d) => {
              setActivePresetId(null);
              setDraftFrom(d);
            }}
            onPeriodToChange={(d) => {
              setActivePresetId(null);
              setDraftTo(d);
            }}
            onApply={handleApplyPeriod}
            resolvedPeriod={periodQuery.data}
            activePresetId={activePresetId}
            onPresetSelect={handlePresetSelect}
          />

          {error && (
            <div className="alert alert-danger mb-4">
              <i className="bi bi-exclamation-triangle me-2" />
              {t("app.analytics.errorLabel")}: {error}
            </div>
          )}

          {loading && data && (
            <div className="text-muted small mb-2">
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              {t("app.analytics.loading")}
            </div>
          )}

          {!error && data && <ViewComponent data={data} />}
        </Tab.Pane>
      </Tab.Content>
    </Tab.Container>
  );
};

export default RoleDashboardStats;
